import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import React, { FC } from 'react';
import { useAppSelector } from '../../app/hooks';
import { TranscriptTypes } from '../../constants/enums';
import { selectIsHitl7CenterPaneEnabled } from '../../redux/features/config/config.selector';
import Colors from '../../utils/color';
import {
  CENTRE_PANE_FONT_SIZES,
  CentrePaneCase,
  CentrePaneFontSize,
  CentrePaneStyle,
  DisplayPreference,
  TranscriptStyle,
} from '../../utils/displayPreference';
import { HITL3_PADDING } from '../../utils/hitl3Spacing';
import ModGroupRow from '../ItemBuilder/components/ModGroupRow';
import ModOptionRow from '../ItemBuilder/components/ModOptionRow';
import OptionDrawer from '../ItemBuilder/components/OptionDrawer';
import { TranscriptMessage } from '../TranscriptMessage';
import { DisplayPreferenceOverride } from './DisplayPreferenceContext';

// A fixed stamp, so the bubble preview shows the clock the console would.
const PREVIEW_TIMESTAMP = '2026-01-01T12:04:31Z';

const TRANSCRIPT_OPTIONS: { value: TranscriptStyle; label: string }[] = [
  { value: 'plain', label: 'Plain lines' },
  { value: 'bubble', label: 'Bubbles' },
];

const STYLE_OPTIONS: { value: CentrePaneStyle; label: string }[] = [
  { value: 'rows', label: 'Rows' },
  { value: 'boxes', label: 'Boxes' },
];

const CASE_OPTIONS: { value: CentrePaneCase; label: string }[] = [
  { value: 'menu', label: 'As on menu' },
  { value: 'caps', label: 'CAPITALS' },
];

// Labelled as the size itself: the agent is choosing a number they can see
// change in the preview, not a named step.
const SIZE_OPTIONS = CENTRE_PANE_FONT_SIZES.map((size) => ({
  value: String(size),
  label: `${size}`,
}));

// Each style's own option-name size, shown as selected until the agent picks
// one: rows 13px (`ModOptionRow.name`), boxes the 3.0 chip's 12px.
const STYLE_FONT_SIZE: Record<'rows' | 'boxes', CentrePaneFontSize> = {
  rows: 13,
  boxes: 12,
};

// HITL-1696: one sample group for the centre-pane preview — a chosen option, the
// group's default, and one that is 86'd, so every box state is on show.
const PREVIEW_GROUP = {
  label: 'Drink',
  selectedNames: ['Coke'],
  value: 'Coke',
  selectedCount: 1,
  isUnmet: false,
  subText: '(select 1)',
};
const PREVIEW_OPTIONS = [
  { name: 'Coke', isSelected: true, is86ed: false, isDefault: false },
  { name: 'Diet Coke', isSelected: false, is86ed: false, isDefault: true },
  { name: 'Sprite', isSelected: false, is86ed: false, isDefault: false },
  { name: 'Dr Pepper', isSelected: false, is86ed: true, isDefault: false },
];

const previewSx = {
  mt: 1.5,
  py: HITL3_PADDING,
  border: `1px solid ${Colors.alto}`,
  borderRadius: 1,
  backgroundColor: Colors.white,
  // Inert: a word click in the transcript files an ASR report and an option
  // click would change a cart — a preview must do neither.
  pointerEvents: 'none' as const,
  userSelect: 'none' as const,
};

const headingSx = {
  fontSize: '0.8rem',
  fontWeight: 700,
  color: Colors.daintree,
  mb: 1,
};

const toggleSx = {
  '& .MuiToggleButton-root': {
    textTransform: 'none',
    fontSize: '0.75rem',
    fontWeight: 600,
    px: 2,
    py: 0.5,
  },
};

interface ChoiceProps<T extends string> {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (next: T) => void;
}

const Choice = <T extends string>({
  label,
  value,
  options,
  onChange,
}: ChoiceProps<T>) => (
  <ToggleButtonGroup
    exclusive
    size="small"
    value={value}
    // `exclusive` hands back null when the selected button is clicked again;
    // ignoring that keeps one option always chosen.
    onChange={(_event, next: T | null) => {
      if (next) onChange(next);
    }}
    aria-label={label}
    sx={toggleSx}
  >
    {options.map((option) => (
      <ToggleButton key={option.value} value={option.value}>
        {option.label}
      </ToggleButton>
    ))}
  </ToggleButtonGroup>
);

interface DisplayPreferenceTabProps {
  value: DisplayPreference;
  onChange: (next: DisplayPreference) => void;
}

/**
 * HITL-1695 — the "Display" tab of the Preferences dialog (7.0 only).
 *
 * The preview is the REAL `TranscriptMessage`, rendered under an override of
 * the staged value, so what the agent sees here cannot drift from what Apply
 * gives them. It is inert: a word click in the transcript files an ASR report,
 * and a preview must never send one.
 *
 * HITL-1696 adds the centre pane: Rows | Boxes and As on menu | CAPITALS,
 * previewed with the real `ModGroupRow` / `OptionDrawer` / `ModOptionRow`.
 */
const DisplayPreferenceTab: FC<DisplayPreferenceTabProps> = ({
  value,
  onChange,
}) => {
  const isPlain = value.transcriptStyle === 'plain';
  const boxed = value.cpStyle === 'boxes';
  const caps = value.cpCase === 'caps';
  const shownFontSize = value.cpFontSize ?? STYLE_FONT_SIZE[value.cpStyle];
  // The centre-pane choices only style the HITL-1616 rows, which exist only
  // behind `hitl_7_center_pane`. Without it they would be controls that do
  // nothing, so the section is not offered.
  const isCenterPaneEnabled = useAppSelector(selectIsHitl7CenterPaneEnabled);

  return (
    <Box data-testid="display-preference-tab">
      <Typography sx={headingSx}>Transcript</Typography>
      <Choice
        label="Transcript style"
        value={value.transcriptStyle}
        options={TRANSCRIPT_OPTIONS}
        onChange={(transcriptStyle) => onChange({ ...value, transcriptStyle })}
      />

      <Box
        data-testid="display-preference-transcript-preview"
        aria-hidden
        sx={{
          ...previewSx,
          // The console's own side padding for each style (LiveTranscriptCard):
          // 4px for the line, the 3.0 padding the bubble's tail needs.
          px: isPlain ? '4px' : HITL3_PADDING,
        }}
      >
        <DisplayPreferenceOverride pref={value}>
          <TranscriptMessage
            text="Hi, what can I get for you today?"
            type={TranscriptTypes.AI}
            compact
            timestamp={PREVIEW_TIMESTAMP}
          />
          <TranscriptMessage
            text="Can I get a large combo with a coke"
            type={TranscriptTypes.CX}
            compact
            timestamp={PREVIEW_TIMESTAMP}
          />
        </DisplayPreferenceOverride>
      </Box>

      {isCenterPaneEnabled && (
        <>
          <Typography sx={{ ...headingSx, mt: 3 }}>Centre pane</Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Choice
              label="Option style"
              value={value.cpStyle}
              options={STYLE_OPTIONS}
              onChange={(cpStyle) => onChange({ ...value, cpStyle })}
            />
            <Choice
              label="Text case"
              value={value.cpCase}
              options={CASE_OPTIONS}
              onChange={(cpCase) => onChange({ ...value, cpCase })}
            />
            <Choice
              label="Text size"
              value={String(shownFontSize)}
              options={SIZE_OPTIONS}
              onChange={(size) =>
                onChange({
                  ...value,
                  cpFontSize: Number(size) as CentrePaneFontSize,
                })
              }
            />
          </Box>

          <Box
            data-testid="display-preference-centre-pane-preview"
            aria-hidden
            sx={{ ...previewSx, px: HITL3_PADDING }}
          >
            <ModGroupRow
              model={PREVIEW_GROUP}
              isOpen
              onToggle={() => {}}
              caps={caps}
            />
            <OptionDrawer boxed={boxed}>
              {PREVIEW_OPTIONS.map((option) => (
                <ModOptionRow
                  key={option.name}
                  name={option.name}
                  isSelected={option.isSelected}
                  is86ed={option.is86ed}
                  isDefault={option.isDefault}
                  bringsFurtherGroups={false}
                  onSelect={() => {}}
                  boxed={boxed}
                  caps={caps}
                  fontSize={value.cpFontSize}
                />
              ))}
            </OptionDrawer>
          </Box>
        </>
      )}
    </Box>
  );
};

export default DisplayPreferenceTab;
